import {
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import queryString from "query-string";
import * as React from "react";
import { useState } from "react";
import BankFindLocations from "../../containers/BankFindLocations";
import { KeyValue, Query, ResultRecord } from "../../interfaces";
import BankFindFinancials from "./BankFindFinancials";
import BankFindHistory from "./BankFindHistory";
import BankFindInstitutionDetails from "./BankFindInstitutionDetails";
import BankFindInstitutionProfile from "./BankFindInstitutionProfile";
import BankFindOtherNames from "./BankFindOtherNames";

interface Props {
  results: ResultRecord;
  appState: Query;
  updateUi: (uiField: string, uiValue: string) => void;
  location?: any;
}

const StyledToggleButtonGroup = withStyles((theme: Theme) => ({
  grouped: {
    "margin": "18px 18px 0 18px",
    "&:first-child": {
      marginLeft: 0,
    },
    "&:last-child": {
      marginRight: 0,
    },
    "&:not(:first-child)": {
      borderLeft: "1px solid #ced6e3",
    },
  },
  root: {
    width: "100%",
    borderBottom: "1px solid #ABC8DF",
  },
}))(ToggleButtonGroup);

const StyledToggleButton = withStyles((theme: Theme) => ({
  root: {
    "borderRadius": 2,
    "border": "1px solid #ced6e3",
    "backgroundColor": "#F2F2F2",
    "boxSizing": "border-box",
    "color": theme.palette.primary.main,
    "&$selected": {
      "backgroundColor": theme.palette.primary.main,
      "color": theme.palette.common.white,
      "&:hover, &.Mui-focusVisible": {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
      },
    },
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
    "&:focus": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    "height": 35,
    "width": "100%",
    "fontSize": 14,
    "fontWeight": "bold",
    "textTransform": "none",
    "marginBottom": -1,
    "borderBottom": "1px solid #ABC8DF",
    "padding": 0,
    "@media (max-width: 685px)": {
      fontSize: 13,
    },
    "@media (max-width: 615px)": {
      fontSize: 12,
    },
    "@media (max-width: 585px)": {
      fontSize: 11,
    },
  },
  selected: {
    borderRadius: 2,
    border: "1px solid #ced6e3",
    boxSizing: "border-box",
  },
}))(ToggleButton);

export default function BankFindDetailsMenu({
  results,
  appState,
  updateUi,
  location,
}: Props) {
  const options = [
    "Institution Details",
    "Locations",
    "History",
    "Financials",
    "Other Names",
  ];

  const [locationValues, setLocationValues] = React.useState("");

  React.useEffect(() => {
    //used to set location filters for table
    const locationOptions =
      (appState.filters.stalp ||
        appState.filters.zip ||
        appState.search.city) &&
      appState.ui.branchOffices;
    const locationFilters = [];
    const locationSearch = [];
    if (appState.filters.zip) {
      locationFilters.push(`ZIP:"${appState.filters.zip}"`);
    }
    if (appState.filters.stalp) {
      locationFilters.push(`STALP:"${appState.filters.stalp}"`);
    }
    if (appState.search.city) {
      locationSearch.push(`CITY:${appState.search.city}`);
    }

    if (locationOptions) {
      updateUi("bankfindLevelThreeView", "Location");

      setLocationValues(
        `${
          locationFilters.length > 0
            ? ` AND ${locationFilters.join(" AND ")}`
            : ""
        }${
          locationSearch.length > 0
            ? `&search=${locationSearch.join(" AND ")}`
            : ""
        }`,
      );
    } else {
      setLocationValues("");
    }
  }, [
    appState.filters.stalp,
    appState.filters.zip,
    appState.search.city,
    appState.ui.branchOffices,
    updateUi,
  ]);

  const [levelThreeView, setLevelThreeView] = useState<string>(() => {
    if (location && location.search) {
      const searchParams = queryString.parse(location.search);

      return searchParams["bankfindLevelThreeView"] &&
        options.includes(searchParams["bankfindLevelThreeView"] as string)
        ? (searchParams["bankfindLevelThreeView"] as string)
        : "Locations";
    }

    return "Institution Details";
  });

  function changeOption(event: React.MouseEvent<HTMLElement>, option: string) {
    if (option) {
      setLevelThreeView(option);
      updateUi("bankfindLevelThreeView", option);
    }
  }

  function getLevelThreeView() {
    const menuTitle: KeyValue = {
      "Institution Details": <BankFindInstitutionDetails results={results} />,
      "Locations": (
        <BankFindLocations
          cert={results.CERT}
          locationsSearch={locationValues}
        />
      ),
      "History": (
        <BankFindHistory cert={results.CERT} ultCert={results.ULTCERT} />
      ),
      "Financials": <BankFindFinancials institution={results} />,
      "Institution Profile": <BankFindInstitutionProfile results={results} />,
      "Other Names": <BankFindOtherNames results={results} />,
    };

    return results.ACTIVE ||
      [
        "History",
        "Institution Profile",
        "Other Names",
        "Institution Details",
        "Financials",
      ].includes(levelThreeView) ? (
      menuTitle[levelThreeView]
    ) : (
      <Typography
        id="inactive-institutions-info"
        color="primary"
        style={{ fontSize: "1rem", textAlign: "center" }}
      >
        This information is not available for inactive institutions.
      </Typography>
    );
  }
  return (
    <>
      <StyledToggleButtonGroup
        id="bankfind-details-menu"
        size="large"
        exclusive
        value={levelThreeView}
        onChange={changeOption}
        aria-label="level three selection"
        sx={{ marginBottom: 2 }}
      >
        {options.map((option: string) => (
          <StyledToggleButton
            id={`toggle-${option.toLowerCase().replace(" ", "-")}-button`}
            value={option}
            key={option}
          >
            {option}
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>
      {getLevelThreeView()}
    </>
  );
}
